import React, { useCallback } from 'react';
import { Table, TableColumn, Badge, Pagination } from '../../../design-system';
import { formatDate, formatRelativeTime } from '../../../utils/dateUtils';
import { useAdminStore } from '../../../store/adminStore';
import { OnboardingApplication } from '../../../services/adminApi';
import styles from './ApplicationsTable.module.css';

interface ApplicationsTableProps {
  applications: OnboardingApplication[];
  isLoading: boolean;
  totalItems: number;
  onRowClick: (application: OnboardingApplication) => void;
}

export const ApplicationsTable: React.FC<ApplicationsTableProps> = ({
  applications,
  isLoading,
  totalItems,
  onRowClick,
}) => {
  const {
    currentPage,
    itemsPerPage,
    setCurrentPage,
    setItemsPerPage,
  } = useAdminStore();

  const handleRowClick = useCallback(
    (row: OnboardingApplication) => {
      onRowClick(row);
    },
    [onRowClick]
  );

  const columns: TableColumn<OnboardingApplication>[] = [
    {
      key: 'store_name',
      label: 'Business Name',
      width: '25%',
      sortable: true,
      render: (value) => <span className={styles.businessName}>{value}</span>,
    },
    {
      key: 'email',
      label: 'Contact Email',
      width: '25%',
      sortable: false,
      render: (value) => (
        <a href={`mailto:${value}`} className={styles.email}>
          {value}
        </a>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '15%',
      sortable: true,
      render: (value) => (
        <Badge status={value as any}>
          {value === 'PENDING'
            ? 'Pending Review'
            : value === 'APPROVED'
            ? 'Approved'
            : (value === 'REJECTED' || value === 'DENIED')
            ? 'Rejected'
            : value}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      label: 'Submitted',
      width: '20%',
      sortable: true,
      render: (value) => (
        <span title={formatDate(value)}>
          {formatRelativeTime(value)}
        </span>
      ),
    },
    {
      key: 'id',
      label: 'Action',
      width: '15%',
      sortable: false,
      render: () => (
        <button
          className={styles.reviewButton}
          type="button"
        >
          Review
        </button>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <Table
        columns={columns}
        data={applications}
        keyField="id"
        isLoading={isLoading}
        isEmpty={applications.length === 0}
        emptyMessage="No applications found"
        onRowClick={handleRowClick}
        rowClassName={(row) =>
          row.status === 'PENDING' ? styles.pendingRow : ''
        }
      />

      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(totalItems / itemsPerPage)}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
        showItemsPerPage={true}
        maxVisiblePages={5}
      />
    </div>
  );
};
